import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { Box, Flex } from '@rebass/grid';
import { Button, Card, CardContent, Divider, IconButton, Typography } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import get from 'lodash.get';
import PageLayout from '../page-layout';
import { AppContext } from '../../app';
import { useIsMobile } from '../../lib/hooks/use-is-mobile';
import { useUser } from '../../lib/hooks/use-user';
import { useNewsFeed } from '../../lib/hooks/use-news-feed';
import { useMarkNewsFeedSeen } from '../../lib/hooks/use-news-feed-unread';
import { createNewsPost } from '../../lib/mutations/create-news-post';
import { addNewsPostImage } from '../../lib/mutations/add-news-post-image';
import { updateNewsPost } from '../../lib/mutations/update-news-post';
import { deleteNewsPost } from '../../lib/mutations/delete-news-post';
import { LoadingDots } from '../atom/loading-dots';
import PostFormSheet from './post-form-sheet';
import DeleteConfirmSheet from './delete-confirm-sheet';
import PostActionsSheet from './post-actions-sheet';
import PostReadSheet from './post-read-sheet';
import PostAuthor from './post-author';
import PostContent from './post-content';
import { colors, scrims } from '../../config/theme';

const styles = {
  statusCard: {
    marginBottom: '16px',
  },
  moreButton: {
    padding: '6px',
    background: scrims.highlight,
  },
  floatingAddButton: {
    position: 'fixed',
    right: '16px',
    bottom: '16px',
    width: '60px',
    height: '60px',
    borderRadius: '999px',
    background: colors.primary,
    color: colors.onPrimary,
    boxShadow: `0 14px 30px ${scrims.primaryGlow}`,
    zIndex: 1200,
    '&:hover': {
      background: colors.primaryDark,
    },
    '@media (max-width: 600px)': {
      right: '12px',
      bottom: '12px',
      width: '56px',
      height: '56px',
    },
  },
  newPostButton: {
    background: colors.primary,
    color: colors.onPrimary,
    '&:hover': {
      background: colors.primaryDark,
    },
  },
};

const emptyDraft = {
  title: '',
  body: '',
  notify_users: false,
  image_url: '',
  linksText: '',
};

const NewsFeed = ({ classes }) => {
  const { loggedInUserId } = useContext(AppContext);
  const queryClient = useQueryClient();
  const { isMobile } = useIsMobile();
  const { user = {} } = useUser(loggedInUserId);
  const permissions = get(user, 'permissions', []);
  const isAdmin = permissions.includes('admin');
  const gymUid = get(user, 'gym.uuid');
  const { posts = [], postsLoading, error } = useNewsFeed(gymUid);
  const markNewsFeedSeen = useMarkNewsFeedSeen(gymUid, loggedInUserId);

  useEffect(() => {
    markNewsFeedSeen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gymUid, loggedInUserId]);

  const [draft, setDraft] = useState(emptyDraft);
  const [formError, setFormError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [formSheet, setFormSheet] = useState(null); // { mode: 'create' | 'edit', postId }
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [menuPost, setMenuPost] = useState(null);
  const [readPost, setReadPost] = useState(null);

  const sortedPosts = useMemo(() => {
    return [...posts].sort((a, b) => {
      const aDate = new Date(a.created_at || a.createdAt || 0).getTime();
      const bDate = new Date(b.created_at || b.createdAt || 0).getTime();
      return bDate - aDate;
    });
  }, [posts]);

  const createMutation = useMutation(
    (postData) => createNewsPost(gymUid, postData),
    {
      onSuccess: () => {
        setFormError('');
        setSuccessMessage('Update published. Members can see it now.');
        setFormSheet(null);
        setDraft(emptyDraft);
        queryClient.invalidateQueries(['news-feed', gymUid]);
      },
      onError: (error) => {
        setSuccessMessage('');
        setFormError(error.message || 'Unable to publish post right now.');
      },
    },
  );

  const updateMutation = useMutation(
    ({ postId, postData }) => updateNewsPost(gymUid, postId, postData),
    {
      onSuccess: () => {
        setFormError('');
        setSuccessMessage('Update saved.');
        setFormSheet(null);
        setDraft(emptyDraft);
        queryClient.invalidateQueries(['news-feed', gymUid]);
      },
      onError: (error) => {
        setSuccessMessage('');
        setFormError(error.message || 'Unable to update post right now.');
      },
    },
  );

  const deleteMutation = useMutation(
    (postId) => deleteNewsPost(gymUid, postId),
    {
      onSuccess: () => {
        setSuccessMessage('Post deleted.');
        setDeleteTargetId(null);
        queryClient.invalidateQueries(['news-feed', gymUid]);
      },
      onError: (error) => {
        setFormError(error.message || 'Unable to delete post right now.');
        setDeleteTargetId(null);
      },
    },
  );

  const openCreateSheet = () => {
    setSuccessMessage('');
    setFormError('');
    setDraft(emptyDraft);
    setFormSheet({ mode: 'create', postId: null });
  };

  const openEditSheet = (post) => {
    setSuccessMessage('');
    setFormError('');
    setDraft({
      title: post.title || '',
      body: post.body || '',
      notify_users: Boolean(post.notify_users),
      image_url: post.image_url || '',
      linksText: Array.isArray(post.links) ? post.links.join('\n') : '',
    });
    setFormSheet({ mode: 'edit', postId: post.id });
  };

  const closeFormSheet = () => {
    setFormSheet(null);
    setFormError('');
    setDraft(emptyDraft);
  };

  const handleSubmit = () => {
    setSuccessMessage('');

    if (!draft.title.trim() || !draft.body.trim()) {
      setFormError('Title and update are both required.');
      return;
    }

    const postPayload = {
      title: draft.title.trim(),
      body: draft.body.trim(),
      notify_users: draft.notify_users,
      image_url: draft.image_url,
      links: draft.linksText
        .split(/\n|,/)
        .map((item) => item.trim())
        .filter(Boolean),
    };

    if (formSheet?.mode === 'edit') {
      updateMutation.mutate({
        postId: formSheet.postId,
        postData: postPayload,
      });
      return;
    }

    createMutation.mutate(postPayload);
  };

  const handleConfirmDelete = () => {
    if (!deleteTargetId) return;
    deleteMutation.mutate(deleteTargetId);
  };

  const handleMenuEdit = () => {
    const post = menuPost;
    setMenuPost(null);
    openEditSheet(post);
  };

  const handleMenuDelete = () => {
    setDeleteTargetId(menuPost.id);
    setMenuPost(null);
  };

  const handleImageUpload = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;

    setIsUploadingImage(true);
    setFormError('');

    addNewsPostImage(file)
      .then((imageUrl) => {
        setDraft((current) => ({
          ...current,
          image_url: imageUrl,
        }));
      })
      .catch(() => {
        setFormError('Unable to upload image right now.');
      })
      .finally(() => {
        setIsUploadingImage(false);
      });
  };

  return (
    <PageLayout selected="news-feed" pageTitle="News Feed">
      {isAdmin && gymUid && !isMobile && (
        <Flex justifyContent="flex-end" mb="16px">
          <Button
            variant="contained"
            className={classes.newPostButton}
            startIcon={<AddIcon />}
            onClick={openCreateSheet}
          >
            New Post
          </Button>
        </Flex>
      )}

      {!gymUid && (
        <Card className={classes.statusCard}>
          <CardContent>
            <Typography variant="body1">No gym selected.</Typography>
            <Typography variant="body2">
              Sign in with a gym account to view or publish gym updates.
            </Typography>
          </CardContent>
        </Card>
      )}

      {!!error && (
        <Card className={classes.statusCard}>
          <CardContent>
            <Typography color="error" variant="body1">
              Unable to load the news feed.
            </Typography>
            <Typography variant="body2">
              {error.message || 'Please try again in a moment.'}
            </Typography>
          </CardContent>
        </Card>
      )}

      {successMessage && (
        <Box mb="16px">
          <Typography style={{ color: colors.successLight }}>{successMessage}</Typography>
        </Box>
      )}

      {postsLoading && gymUid && <LoadingDots />}

      {!postsLoading && !error && gymUid && sortedPosts.length === 0 && (
        <Card>
          <CardContent>
            <Typography variant="body1">No updates yet.</Typography>
            <Typography variant="body2">
              {isAdmin
                ? 'Create the first post to share announcements with members.'
                : 'Check back soon for gym announcements.'}
            </Typography>
          </CardContent>
        </Card>
      )}

      {!postsLoading &&
        !error &&
        gymUid &&
        sortedPosts.map((post, index) => {
          const author =
            post.author_name ||
            post.author?.name ||
            post.created_by_name ||
            'Gym staff';
          const createdAt = post.created_at || post.createdAt;

          return (
            <Box key={post.id || `${post.title}-${index}`} mb="16px">
              <Card style={{ cursor: 'pointer' }} onClick={() => setReadPost(post)}>
                <CardContent>
                  <Flex justifyContent="space-between" alignItems="flex-start">
                    <PostAuthor
                      authorId={post.author_id}
                      fallbackName={author}
                      createdAt={createdAt}
                    />
                    {isAdmin && (
                      <IconButton
                        className={classes.moreButton}
                        onClick={(event) => {
                          event.stopPropagation();
                          setMenuPost(post);
                        }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Flex>
                  <Box mt="12px">
                    <Typography variant="h6">
                      {post.title || 'Update'}
                    </Typography>
                  </Box>
                  <Divider style={{ margin: '16px 0' }} />
                  <PostContent post={post} truncateBody />
                </CardContent>
              </Card>
            </Box>
          );
        })}

      <PostFormSheet
        open={!!formSheet}
        onClose={closeFormSheet}
        mode={formSheet?.mode}
        draft={draft}
        setDraft={setDraft}
        onSubmit={handleSubmit}
        isSubmitting={createMutation.isLoading || updateMutation.isLoading}
        isUploadingImage={isUploadingImage}
        onImageUpload={handleImageUpload}
        error={formError}
        authorName={get(user, 'name')}
        authorAvatarUrl={get(user, 'avatar_url')}
      />

      <DeleteConfirmSheet
        open={!!deleteTargetId}
        onClose={() => setDeleteTargetId(null)}
        onConfirm={handleConfirmDelete}
        isDeleting={deleteMutation.isLoading}
      />

      <PostActionsSheet
        open={!!menuPost}
        onClose={() => setMenuPost(null)}
        onEdit={handleMenuEdit}
        onDelete={handleMenuDelete}
      />

      <PostReadSheet open={!!readPost} onClose={() => setReadPost(null)} post={readPost} />

      {isAdmin && gymUid && isMobile && (
        <IconButton
          aria-label="Create post"
          className={classes.floatingAddButton}
          onClick={openCreateSheet}
        >
          <AddIcon style={{ fontSize: 30 }} />
        </IconButton>
      )}
    </PageLayout>
  );
};

export default withStyles(styles)(NewsFeed);
